import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../../hooks/useBooking';
import { hexToRgba } from '../../lib/colorUtils';
import { primaryButtonStyle, useSiteColors } from '../../hooks/useSiteColors';
import {
  normalizeListingSocials,
  sortListingSocials,
} from '../../utils/socials';

export function NavBar() {
  const navigate = useNavigate();
  const { businessListing, reviews, locations } = useBooking();
  const c = useSiteColors();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const galleryImages = businessListing?.cover_images ?? [];

  const socialEntries = useMemo(() => {
    const list = normalizeListingSocials(businessListing?.socials);
    return sortListingSocials(list ?? []).filter(Boolean);
  }, [businessListing?.socials]);

  const showAboutSection =
    Boolean(businessListing?.about?.trim()) ||
    galleryImages.length > 0 ||
    socialEntries.length > 0;
  const hasReviews = reviews.length > 0;
  const hasLocationData =
    locations.length > 0 ||
    Boolean(businessListing?.address?.trim()) ||
    Boolean(businessListing?.google_place_id?.trim());
  const hasFaq = (businessListing?.faq?.length ?? 0) > 0 || Boolean(businessListing?.cancellation_policy_text);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileOpen(false);
  };

  const handleBookNow = () => navigate('/book');

  if (!businessListing) return null;

  const ctaLabel = businessListing.cta_label ?? 'Book Now';

  const navBg = hexToRgba(c.background, scrolled ? 0.95 : 0.85);
  const linkColor = c.mutedText;
  const linkHoverColor = c.text;

  const NavLinks = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      <button
        onClick={() => scrollToSection('services')}
        className={`text-sm font-medium transition-colors ${mobile ? 'text-left py-2' : ''}`}
        style={{ color: linkColor }}
        onMouseEnter={(e) => (e.currentTarget.style.color = linkHoverColor)}
        onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
      >
        Services
      </button>
      {showAboutSection && (
        <button
          onClick={() => scrollToSection('about')}
          className={`text-sm font-medium transition-colors ${mobile ? 'text-left py-2' : ''}`}
          style={{ color: linkColor }}
          onMouseEnter={(e) => (e.currentTarget.style.color = linkHoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
        >
          About
        </button>
      )}
      {hasReviews && (
        <button
          onClick={() => scrollToSection('reviews')}
          className={`text-sm font-medium transition-colors ${mobile ? 'text-left py-2' : ''}`}
          style={{ color: linkColor }}
          onMouseEnter={(e) => (e.currentTarget.style.color = linkHoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
        >
          Reviews
        </button>
      )}
      {hasLocationData && (
        <button
          onClick={() => scrollToSection('locations')}
          className={`text-sm font-medium transition-colors ${mobile ? 'text-left py-2' : ''}`}
          style={{ color: linkColor }}
          onMouseEnter={(e) => (e.currentTarget.style.color = linkHoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
        >
          Locations
        </button>
      )}
      <button
        onClick={() => scrollToSection('location')}
        className={`text-sm font-medium transition-colors ${mobile ? 'text-left py-2' : ''}`}
        style={{ color: linkColor }}
        onMouseEnter={(e) => (e.currentTarget.style.color = linkHoverColor)}
        onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
      >
        Contact
      </button>
      {hasFaq && (
        <button
          onClick={() => scrollToSection('faq')}
          className={`text-sm font-medium transition-colors ${mobile ? 'text-left py-2' : ''}`}
          style={{ color: linkColor }}
          onMouseEnter={(e) => (e.currentTarget.style.color = linkHoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
        >
          FAQ
        </button>
      )}
    </>
  );

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: navBg,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${c.border}`,
        boxShadow: scrolled ? `0 1px 3px ${hexToRgba(c.text, 0.04)}` : 'none',
      }}
    >
      <div className="max-w-[1200px] mx-auto px-6 h-[72px] flex items-center justify-between">
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center gap-3"
        >
          {businessListing.logo_url && (
            <img src={businessListing.logo_url} alt={businessListing.business_name} className="h-8 object-contain" />
          )}
          <span className="text-xl font-bold" style={{ color: c.text }}>
            {businessListing.business_name}
          </span>
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-9">
          <button
            onClick={handleBookNow}
            className="text-sm font-semibold px-6 py-2.5 rounded-full transition-all hover:-translate-y-0.5"
            style={primaryButtonStyle(c)}
          >
            {ctaLabel}
          </button>
          <NavLinks />
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Menu"
          aria-expanded={mobileOpen}
        >
          <div className="space-y-1.5">
            <span className="block w-6 h-0.5 rounded-full" style={{ backgroundColor: c.text }} />
            <span className="block w-6 h-0.5 rounded-full" style={{ backgroundColor: c.text }} />
            <span className="block w-6 h-0.5 rounded-full" style={{ backgroundColor: c.text }} />
          </div>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden px-6 pb-4">
          <div className="flex flex-col gap-3">
            <NavLinks mobile />
            <button
              onClick={() => { handleBookNow(); setMobileOpen(false); }}
              className="text-sm font-semibold px-6 py-2.5 rounded-full w-full"
              style={primaryButtonStyle(c, 0)}
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
